import type { MonetaryAmount } from '@backmarket/http-api/src/api-models/MonetaryAmount'
import type { Country } from '@backmarket/http-api/src/standards/Country'

export type KybMetadataResponse = {
  secondhandDealerNumberIssuingPrefectures: {
    label: string
    value: string
  }[]
  registrationTypes: {
    label: string
    value: string
  }[]
}

export type KybFile = {
  id: string
  filename: string
}

export enum StakeholderRole {
  ACCOUNT_REQUESTER = 'ACCOUNT_REQUESTER',
  LEGAL_REPRESENTATIVE = 'LEGAL_REPRESENTATIVE',
  PRIMARY_BENEFICIAL_OWNER = 'PRIMARY_BENEFICIAL_OWNER',
  SECONDARY_BENEFICIAL_OWNER = 'SECONDARY_BENEFICIAL_OWNER',
}

export type Address = {
  addressLine1: string
  addressLine2: string | null
  postalCode: string
  city: string
  stateOrProvince: string | null
  country: Country
}

/**
 * Identification types for stakeholders
 * Based on product requirements: Only National Identity Card and Passport
 */
export enum IdentificationTypes {
  PASSPORT = 'PASSPORT',
  NATIONAL_ID = 'NATIONAL_ID_CARD',
}

type PassportIdentityDocument = {
  identificationType: IdentificationTypes
  countryOfIssue: Country
  identificationNumber: string
  front: KybFile
  back: null
}

type NationalIdentityDocument = {
  identificationType: IdentificationTypes
  countryOfIssue: Country
  identificationNumber: string
  front: KybFile
  back: KybFile
}

export type Stakeholder = {
  id: string
  firstName: string
  kanaFirstName?: string
  latinFirstName?: string
  lastName: string
  kanaLastName?: string
  latinLastName?: string
  dateOfBirth: string
  countryOfBirth: Country
  countryOfCitizenship: Country
  phoneNumber: string
  address: Address
  identityDocument: PassportIdentityDocument | NationalIdentityDocument | null
  roles: StakeholderRole[]
}

export type StakeholderInput = Omit<Stakeholder, 'id'> & {
  id: Stakeholder['id'] | null
}

export enum KybStepCode {
  BUSINESS_DETAILS = 'BUSINESS_DETAILS',
  ACCOUNT_REQUESTER_DETAILS = 'ACCOUNT_REQUESTER_DETAILS',
  LEGAL_REPRESENTATIVE_DETAILS = 'LEGAL_REPRESENTATIVE_DETAILS',
  PRIMARY_BENEFICIAL_OWNER_DETAILS = 'PRIMARY_BENEFICIAL_OWNER_DETAILS',
  LETTER_OF_AUTHORIZATION = 'LETTER_OF_AUTHORIZATION',
  REVIEW = 'REVIEW',
}

export enum KybStepStatus {
  TODO = 'TODO',
  DONE = 'DONE',
  ERROR = 'ERROR',
  SKIP = 'SKIP',
}

export type KybStep = {
  value: KybStepCode
  label: string
  status: KybStepStatus
}

export type BusinessDetails = {
  legalName: string
  tradeName: string
  kanaLegalName?: string
  latinLegalName?: string
  registrationCountry: string
  registrationType: string
  registrationNumber: string
  incorporationDate: string
  headquartersAddress: Address
  branchesAddresses: Address[]
  registeredBusinessAddress: Address
  email: string
  investedCapital?: MonetaryAmount
  articleOfIncorporation?: KybFile
  secondhandDealerNumber?: string
  secondhandDealerNumberIssuingPrefecture?: string
  secondhandDealerLicence?: KybFile
  w9?: KybFile
}

export type KybResponse = {
  steps: KybStep[]
  data: {
    business?: BusinessDetails
    stakeholders: Stakeholder[]
    letterOfAuthorization: KybFile | null
  }
  errors: {
    entityId: string | null
    field: string
    message: string
  }[]
}

export type KybData = KybResponse['data']

export type LegalRepresentativeBody = {
  legalRepresentative: Stakeholder & {
    sameAsAccountRequester: boolean
  }
}

export type PrimaryBeneficialOwnerBody = {
  primary: Stakeholder & {
    sameAsAccountRequester: boolean
    sameAsLegalRepresentative: boolean
  }
  secondary: Stakeholder[]
}
